import React from "react"
import { useTranslation } from "react-i18next"

import { BillCsvPreview } from "@/components/bill-csv-preview/bill-csv-preview"
import { BillPdfViewerDialog } from "@/components/bill-pdf-viewer-dialog"
import { JsonPreview } from "@/components/json-preview/json-preview"
import { DialogTitle } from "@/nzds/feedback/dialog-title/dialog-title"
import { HStack } from "@/nzds/layout"
import { useBillDocumentPreview } from "@/services/bill/use-bill-document-preview"
import type { BillPeriod } from "@/services/core-api/types"

import { Button, Dialog, Skeleton, Stack, Typography } from "@mui/material"

const isCsvDocument = (
  contentType: string | null,
  originalFilename: string | null
): boolean => {
  const normalizedContentType = contentType?.toLowerCase()
  const normalizedFilename = originalFilename?.toLowerCase()

  return (
    normalizedContentType?.includes("csv") === true ||
    normalizedFilename?.endsWith(".csv") === true
  )
}

const isJsonDocument = (
  contentType: string | null,
  originalFilename: string | null
): boolean => {
  const normalizedContentType = contentType?.toLowerCase()
  const normalizedFilename = originalFilename?.toLowerCase()

  return (
    normalizedContentType?.includes("json") === true ||
    normalizedFilename?.endsWith(".json") === true
  )
}

const isPdfDocument = (
  contentType: string | null,
  originalFilename: string | null
): boolean => {
  const normalizedContentType = contentType?.toLowerCase()
  const normalizedFilename = originalFilename?.toLowerCase()

  return (
    normalizedContentType?.includes("pdf") === true ||
    normalizedFilename?.endsWith(".pdf") === true
  )
}

interface BillDocumentSectionProps {
  /** A bill or a bill period: both carry these source-file fields. */
  bill: Pick<
    BillPeriod,
    "id" | "organization_id" | "raw_file" | "raw_file_id"
  > | null
  isLoading: boolean
  pdfOnly?: boolean
}

const BillDocumentActionsSkeleton: React.FC = () => (
  <HStack sx={{ gap: 1 }}>
    <Skeleton height={40} width={120} />
    <Skeleton height={40} width={120} />
  </HStack>
)

export const BillDocumentSection: React.FC<BillDocumentSectionProps> = ({
  bill,
  isLoading,
  pdfOnly = false,
}) => {
  const { t } = useTranslation("sites")
  const { t: tCommon } = useTranslation("common")
  const [isViewerOpen, setIsViewerOpen] = React.useState(false)
  const rawFileId = bill?.raw_file_id
  const downloadUrl = bill?.raw_file?.download_url?.trim()
  const contentType = bill?.raw_file?.content_type ?? null
  const originalFilename = bill?.raw_file?.original_filename ?? null

  const isPdf = isPdfDocument(contentType, originalFilename)
  const isUnavailable =
    !bill || !rawFileId || (pdfOnly && !isPdf) || !downloadUrl
  const allowTextPreview = !(pdfOnly && isPdf)
  const hasInvalidDownloadUrl = (() => {
    if (!downloadUrl) {
      return false
    }

    try {
      new URL(downloadUrl)
      return false
    } catch {
      return true
    }
  })()
  const isCsvPreview =
    !isUnavailable &&
    !hasInvalidDownloadUrl &&
    allowTextPreview &&
    isCsvDocument(contentType, originalFilename)
  const isJsonPreview =
    !isUnavailable &&
    !hasInvalidDownloadUrl &&
    allowTextPreview &&
    !isCsvPreview &&
    isJsonDocument(contentType, originalFilename)
  const needsTextPreview = isCsvPreview || isJsonPreview
  const isPdfPreview = !needsTextPreview

  const { billDocumentData, billDocumentIsError, billDocumentIsLoading } =
    useBillDocumentPreview({
      billId: !isUnavailable ? bill.id : null,
      organizationId: !isUnavailable ? String(bill.organization_id) : "",
      queryOptions: {
        enabled: isViewerOpen && needsTextPreview,
        retry: false,
      },
    })

  if (isLoading) {
    return (
      <Stack sx={{ gap: 1 }}>
        <Typography variant="h3">{t("bills.detail.document.title")}</Typography>
        <BillDocumentActionsSkeleton />
      </Stack>
    )
  }

  if (isUnavailable) {
    return (
      <Stack sx={{ gap: 1 }}>
        <Typography variant="h3">{t("bills.detail.document.title")}</Typography>
        <Typography color="text.secondary" variant="body2">
          {t("bills.detail.document.unavailable")}
        </Typography>
      </Stack>
    )
  }

  if (hasInvalidDownloadUrl) {
    return (
      <Stack sx={{ gap: 1 }}>
        <Typography variant="h3">{t("bills.detail.document.title")}</Typography>
        <Typography color="error.main" role="status" variant="body2">
          {t("bills.detail.document.error")}
        </Typography>
      </Stack>
    )
  }

  return (
    <Stack sx={{ gap: 1 }}>
      <Typography variant="h3">{t("bills.detail.document.title")}</Typography>
      <HStack sx={{ gap: 1 }}>
        <Button
          onClick={() => {
            setIsViewerOpen(true)
          }}
          variant="contained"
        >
          {tCommon("button.view")}
        </Button>
        <Button
          component="a"
          href={downloadUrl}
          rel="noopener noreferrer"
          target="_blank"
          variant="outlined"
        >
          {tCommon("button.download")}
        </Button>
      </HStack>
      {isPdfPreview ? (
        <BillPdfViewerDialog
          downloadUrl={downloadUrl}
          iframeTitle={t("bills.detail.document.iframeTitle")}
          onClose={() => {
            setIsViewerOpen(false)
          }}
          open={isViewerOpen}
          title={t("bills.detail.document.title")}
        />
      ) : (
        <Dialog
          aria-labelledby="bill-document-viewer-title"
          fullScreen
          onClose={() => {
            setIsViewerOpen(false)
          }}
          open={isViewerOpen}
        >
          <DialogTitle
            id="bill-document-viewer-title"
            onClose={() => {
              setIsViewerOpen(false)
            }}
          >
            <Typography variant="h3">
              {t("bills.detail.document.title")}
            </Typography>
          </DialogTitle>
          {isCsvPreview ? (
            <BillCsvPreview
              isError={billDocumentIsError}
              isLoading={billDocumentIsLoading}
              isTruncated={billDocumentData?.truncated === true}
              previewText={billDocumentData?.preview_text ?? ""}
            />
          ) : null}
          {isJsonPreview ? (
            <Stack sx={{ flex: 1, minHeight: 0, p: 2, pt: 0 }}>
              <JsonPreview
                isError={billDocumentIsError}
                isLoading={billDocumentIsLoading}
                isTruncated={billDocumentData?.truncated === true}
                previewText={billDocumentData?.preview_text ?? ""}
              />
            </Stack>
          ) : null}
        </Dialog>
      )}
    </Stack>
  )
}
